import _ from 'lodash';
import {createSelector} from 'reselect';

import {VisualConversationTrackerModel} from '../../components/inboxes/viewer/conversation/topbar/invite/inviteHelpers';
import {hasTrackerSeenAll, isTracker, isTrackerOwner} from '../../helpers/modelHelpers';
import {AppParametricSelector} from '../../helpers/reselectHelpers';
import {ConversationModel} from '../../models/api/conversationModel';
import {
  ConversationTrackerModel,
  ConversationTrackerStagesEnum,
  ConversationTrackerStatusesEnum,
} from '../../models/api/conversationTrackerModel';
import {TeammateAccountStatusesEnum, TeammateModel} from '../../models/api/teammateModel';
import {ConversationSelector, TeammateIdsSelector} from '../data/conversations/conversationsSelectors';
import {FindTeammate, getFindTeammate} from '../data/teammates/lazyTeammatesSelectors';
import {getTeammates, getUserId} from '../data/teammates/teammatesSelectors';

/*
 * Selectors.
 */

export type VisualConversationTrackersSelector<P> = AppParametricSelector<
  P,
  ReadonlyArray<VisualConversationTrackerModel>
>;

export function makeGetInviteParticipantsTrackers<P>(
  getUserOrDelegatedTeammateId: AppParametricSelector<P, number>,
  getConversationInboxesTeammateIds: TeammateIdsSelector<P>,
  getConversation: ConversationSelector<P>,
): VisualConversationTrackersSelector<P> {
  return createSelector(
    [
      getUserId,
      getUserOrDelegatedTeammateId,
      getConversation,
      getFindTeammate,
      getConversationInboxesTeammateIds,
    ],
    (userId, userOrDelegateTeammateId, conversation, findTeammate, conversationInboxesTeammateIds) =>
      computeInviteParticipantsTrackers({
        userId,
        userOrDelegateTeammateId,
        conversation,
        findTeammate,
        conversationInboxesTeammateIds,
      }),
  );
}

type TrackersSelector<P> = AppParametricSelector<P, ReadonlyArray<VisualConversationTrackerModel>>;
export function makeGetInviteParticipantsTeammatesWithAccessCount<P>(
  getConversationInboxTeammateIds: TeammateIdsSelector<P>,
  getTrackers: TrackersSelector<P>,
) {
  return createSelector(
    [getConversationInboxTeammateIds, getTrackers, getTeammates],
    (inboxTeammateIds, trackers, teammates) =>
      _(trackers)
        .map((p) => p.teammate.id)
        .concat(inboxTeammateIds)
        .uniq()
        .map((id) => {
          const teammate = teammates[id];
          return teammate && teammate.accountStatus === TeammateAccountStatusesEnum.ACTIVE && teammate;
        })
        .compact()
        .size(),
  );
}

/*
 * Helpers.
 */

interface ComputeInviteParticipantsTrackersParams {
  userOrDelegateTeammateId: number;
  userId: number;
  conversation: ConversationModel;
  conversationInboxesTeammateIds: ReadonlyArray<TeammateModel['id']>;
  findTeammate: FindTeammate;
}

export function computeInviteParticipantsTrackers({
  userId,
  userOrDelegateTeammateId,
  conversation,
  findTeammate,
  conversationInboxesTeammateIds,
}: ComputeInviteParticipantsTrackersParams): ReadonlyArray<VisualConversationTrackerModel> {
  return _(conversation.trackers)
    .filter((tracker) => isTracker(tracker))
    .compact()
    .map((tracker): VisualConversationTrackerModel | null => {
      const teammate = findTeammate(
        tracker.teammateId,
        'inviteParticipantsSelectors makeGetInviteParticipantsTrackers',
      );
      return teammate
        ? computeVisualConversationTracker({
            conversation,
            tracker,
            teammate,
            userId,
            userOrDelegateTeammateId,
            conversationInboxesTeammateIds,
          })
        : null;
    })
    .compact()
    .sortBy(
      (p) => p.hasMuted && p.hasArchived,
      (p) => !p.hasSeenAll || !p.seenAt,
      (p) => p.hasArchived,
      (p) => p.hasSnoozed,
      (p) => -(p.seenAt || 0),
    )
    .value();
}

interface ComputeVisualConversationTrackerParams {
  conversation: ConversationModel;
  tracker: ConversationTrackerModel;
  teammate: TeammateModel;
  userOrDelegateTeammateId: number;
  userId: number;
  conversationInboxesTeammateIds: ReadonlyArray<TeammateModel['id']>;
}

export function computeVisualConversationTracker({
  conversation,
  tracker,
  teammate,
  userOrDelegateTeammateId,
  userId,
  conversationInboxesTeammateIds,
}: ComputeVisualConversationTrackerParams): VisualConversationTrackerModel {
  return {
    teammate,
    isUser: tracker.teammateId === userId,
    isUserOrDelegatedTeammate: tracker.teammateId === userOrDelegateTeammateId,
    isOwner: isTrackerOwner(tracker),
    isInboxMember: conversationInboxesTeammateIds.includes(teammate.id),
    hasSeenAll: hasTrackerSeenAll(conversation, tracker),
    hasSnoozed: conversation.reminders.some((r) => r.teammateId === teammate.id),
    hasArchived: tracker.status === ConversationTrackerStatusesEnum.ARCHIVED,
    hasMuted: tracker.stage === ConversationTrackerStagesEnum.TRACKER,
    seenAt: tracker.autoSeenAt,
  };
}
